import React, { useRef, useState, useCallback, useEffect } from 'react'
import { ChevronDown, X } from 'lucide-react'
import { cn } from '@/lib/cn'

export interface ComboboxOption {
  value: string
  label: string
}

export interface ComboboxProps {
  loadOptions: (query: string) => Promise<ComboboxOption[]>
  value: string
  onChange: (value: string) => void
  label?: string
  placeholder?: string
  id?: string
  disabled?: boolean
  className?: string
}

const inputBaseClasses =
  'box-border h-11 w-full rounded-xl border border-gray-200 bg-white/95 px-3.5 text-sm text-gray-900 shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-all duration-150 placeholder:text-gray-500 focus:border-primary/40 focus:outline-none focus:ring-4 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-800/90 dark:text-gray-100 dark:placeholder:text-gray-400'

export function Combobox({
  loadOptions,
  value,
  onChange,
  label,
  placeholder = 'Search…',
  id: idProp,
  disabled = false,
  className,
}: ComboboxProps): React.ReactElement {
  const id = idProp ?? `combobox-${Math.random().toString(36).slice(2, 9)}`
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState<ComboboxOption[]>([])
  const [loading, setLoading] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null)

  const selectedOption = options.find((o) => o.value === value)
  const displayText = open ? inputValue : (value ? (selectedOption?.label ?? selectedLabel ?? value) : '')
  const hasValue = value !== ''

  // Returning focus after a choice must not reopen the list over the next field.
  const focusWithoutOpening = useRef(false)
  const refocus = useCallback(() => {
    focusWithoutOpening.current = true
    inputRef.current?.focus()
    focusWithoutOpening.current = false
  }, [])

  const close = useCallback(() => {
    setOpen(false)
    setHighlightedIndex(0)
    setInputValue('')
  }, [])

  const load = useCallback(
    async (query: string) => {
      setLoading(true)
      try {
        const result = await loadOptions(query)
        setOptions(result)
        setHighlightedIndex(0)
      } finally {
        setLoading(false)
      }
    },
    [loadOptions]
  )

  useEffect(() => {
    if (!open) return
    load(inputValue)
  }, [open, inputValue, load])

  useEffect(() => {
    if (open && options.length > 0 && listRef.current) {
      const item = listRef.current.querySelector<HTMLElement>('[data-highlighted="true"]')
      item?.scrollIntoView({ block: 'nearest' })
    }
  }, [open, highlightedIndex, options.length])

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current?.contains(e.target as Node)) return
      close()
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      close()
      refocus()
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, close, refocus])

  const selectOption = useCallback(
    (opt: ComboboxOption) => {
      onChange(opt.value)
      setSelectedLabel(opt.label)
      close()
      refocus()
    },
    [onChange, close, refocus]
  )

  const handleInputFocus = () => {
    if (disabled || focusWithoutOpening.current) return
    setOpen(true)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value)
    setOpen(true)
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      setHighlightedIndex((i) => Math.min(i + 1, options.length - 1))
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((i) => Math.max(i - 1, 0))
    }
    if (e.key === 'Enter' && open && options[highlightedIndex]) {
      e.preventDefault()
      selectOption(options[highlightedIndex])
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      close()
    }
  }

  const handleListKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((i) => Math.min(i + 1, options.length - 1))
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((i) => Math.max(i - 1, 0))
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      const opt = options[highlightedIndex]
      if (opt) selectOption(opt)
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      close()
      refocus()
    }
  }

  useEffect(() => {
    if (!value) setSelectedLabel(null)
  }, [value])

  useEffect(() => {
    if (value && options.length > 0) {
      const opt = options.find((o) => o.value === value)
      if (opt) setSelectedLabel((prev) => prev ?? opt.label)
    }
  }, [value, options])

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      {label && (
        <label
          htmlFor={id}
          className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400"
        >
          {label}
        </label>
      )}
      <div className="relative flex">
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-controls={open ? `${id}-listbox` : undefined}
          aria-activedescendant={
            open && options[highlightedIndex]
              ? `${id}-option-${options[highlightedIndex].value}`
              : undefined
          }
          value={displayText}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onClick={handleInputFocus}
          onKeyDown={handleInputKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          className={cn(
            inputBaseClasses,
            'pr-8',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        />
        <span className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
          {hasValue && (
            <button
              type="button"
              tabIndex={-1}
              aria-label="Clear selection"
              onClick={() => {
                onChange('')
                setSelectedLabel(null)
                inputRef.current?.focus()
              }}
              className="rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 text-gray-500 transition-transform', open && 'rotate-180')}
          />
        </span>
      </div>
      {open && (
        <ul
          ref={listRef}
          id={`${id}-listbox`}
          role="listbox"
          tabIndex={-1}
          onKeyDown={handleListKeyDown}
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-600 dark:bg-gray-800"
        >
          {loading ? (
            <li className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
              Loading…
            </li>
          ) : options.length === 0 ? (
            <li className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
              No results
            </li>
          ) : (
            options.map((opt, i) => (
              <li
                key={opt.value}
                id={`${id}-option-${opt.value}`}
                role="option"
                aria-selected={opt.value === value}
                data-highlighted={i === highlightedIndex}
                onClick={() => selectOption(opt)}
                onMouseEnter={() => setHighlightedIndex(i)}
                className={cn(
                  'cursor-pointer px-3 py-2 text-sm transition-colors',
                  opt.value === value && 'bg-primary/10 text-primary dark:bg-primary/20',
                  i === highlightedIndex && opt.value !== value && 'bg-gray-100 dark:bg-gray-700',
                  i === highlightedIndex && opt.value === value && 'bg-primary/15 dark:bg-primary/25'
                )}
              >
                {opt.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  )
}

Combobox.displayName = 'Combobox'
